import React, { useEffect } from "react";
import Slider from "react-slick";
import { DropdownList } from "react-widgets";
import api from "../../api";
import {
  AppPermissions,
  htmlDecode,
  setDashboardSearchData,
  useHasPermission,
} from "../../core";
import { Project, PropertyDetails } from "../../core/api/models/project";
import moment from "moment";
import { LeadRequirement } from "../../core/api/models/requirement";
import TenancyTimeline from "./TenancyTimeline";
import { RouteKeys } from "../routes/route-keys";
import { DateInput } from "../../components/form-group";
import SplitMerge from "./SliptMerge";
import Spinner from "../../components/loader/spinner";
import MapPreviewImage from "../../assets/images/map-preview.png";
import { ContainerLoader } from "../../components/loader";
import ErrorMessage from "../../components/text/ErrorMessage";
import { useDispatch } from "react-redux";
import { ProjectOverviewTenantsGraphView } from "./project-overview-tenants-graph-view";
import { ProjectOverviewLocationMap } from "./project-overview-location-map";
import { ProjectOverviewBasicDetails } from "./project-overview-basic-details";

// Project Detais PopUp
// ================================================================================================

const ProjectOverview: React.FunctionComponent<{
  project?: Project;
  mergePropeties?: () => void;
  isSpliting?: boolean;
  isMerging?: boolean;
  splitUnitsAndProperties?: (
    propertyId: string,
    unitId: string,
    splitPropertyDetails: any[]
  ) => void;
  searchData?: Project;
  requirementVersion?: number;
  setSelectedProperty?: (propertyDetails: any) => void;
  selectedProperty?: any;
  availableUnit?: number;
  allUnit?: number;
  configuration: LeadRequirement;
  showDetailsOnly?: boolean;
  onView?: (id: string) => void;
  onClose?: () => void;
}> = (props) => {
  const [mainSlider, setMainSlider] = React.useState<any>(null);
  const [thumbSlider, setThumbSlider] = React.useState<any>(null);
  const [isMapEnabled, setIsMapEnabled] = React.useState<boolean>(false);
  const [showAddOption, setShowAddOption] = React.useState<boolean>(false);
  const [showAddSingleOption, setShowAddSingleOption] = React.useState<boolean>(
    false
  );
  const canViewTenancyOrLease = useHasPermission(AppPermissions.ViewTenancy);
  const canAddTenancyOrLease = useHasPermission(AppPermissions.CreateTenancy);
  const [expandedRows, setExpandedRows] = React.useState<{
    [key: number]: boolean;
  }>({});
  const [isShown, setIsShown] = React.useState<boolean>(false);
  const [
    filteredTenantsLoading,
    setFilteredTenantsLoading,
  ] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<Date>();
  const [filteredTenants, setFilteredTenants] = React.useState<any[]>();
  const [filteredUnitDetails, setFilteredUnitDetails] = React.useState<any[]>();
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const dispatch = useDispatch();

  const handleClickpopup = () => {
    setIsShown(!isShown);
  };


  const toggleRow = (rowId: number) => {
    setExpandedRows((prevExpandedRows) => ({
      ...prevExpandedRows,
      [rowId]: !prevExpandedRows[rowId],
    }));
  };

  const onSelect = (unitID: string) => {
    const ut = props.project?.unitDetails?.find((u) => u.unitID === unitID);

    const project = props.searchData;

    const property = ut?.propertyID
      ? project?.properties?.find(
        (p) => p.propertyID?.toString() === ut.propertyID?.toString()
      )
      : undefined;

    props.setSelectedProperty?.([
      ...(props.selectedProperty || []),
      {
        floor: ut?.floorName!,
        unitId: ut?.unitID!,
        unitName: ut?.unitName!,
        area: ut?.unitArea!,
        interiorType: property?.attribute?.interiorType,
        quotedRent: property?.attribute?.quotedRentPerSqFtPerMonth,
        currentStatus: property?.propertyAvailableStatus,
        availableFrom: property?.availablefromdate,
        projectId: project?._id!,
        propertyId: property?._id,
        propertyName: property?.propertyName,
        propertyAddress: property?.propertyAddress1,
        propertyCoverImage: property?.propertyCoverImage,
        propertySqrFeet: property?.propertySqrFeet,
        developer: project?.projectBuilderName!,
        pricePerSqft: property?.pricePerSqft,
        availableUnits: property?.availableUnits,
        requirementVersion: props.requirementVersion,
        projectName: project?.projectName,
      },
    ]);
  };

  const onRemove = (unitID: string) => {
    const selectedProperty = props.selectedProperty?.filter(
      (x: PropertyDetails) => x.unitId !== unitID
    );
    if (selectedProperty.length === 0) {
      setShowAddOption(false);
      setShowAddSingleOption(false);
    }
    props.setSelectedProperty?.(selectedProperty);
  };

  const settings = {
    className: "property-slider mb-3 pb-1",
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
  };
  const thumbSettings = {
    className: "property-thumb-slider",
    dots: false,
    infinite: false,
    slidesToShow: 7,
    slidesToScroll: 1,
    variableWidth: false,
    arrows: false,
    focusOnSelect: true,
  };

  const ordinalSuffixOf = (i: number) => {
    const j = i % 10;
    const k = i % 100;
    if (j === 1 && k !== 11) {
      return i + "st";
    }
    if (j === 2 && k !== 12) {
      return i + "nd";
    }
    if (j === 3 && k !== 13) {
      return i + "rd";
    }
    return i + "th";
  };

  const [selectedOption, setSelectedOption] = React.useState<any>([
    {
      floorName: "",
      unitID: "",
    },
  ]);

  const floors = [
    ...new Set(
      props.project?.unitDetails
        ?.filter(
          (x) =>
            x.propertyID !== "0" &&
            Number(x.propertyID) !== 0 &&
            !props.selectedProperty?.find((o: any) => o.unitId === x.unitID)
        )
        ?.sort((a, b) => (Number(a.floorName) < Number(b.floorName) ? -1 : 1))
        ?.map((f) => f.floorName)
    ),
  ].sort();

  const floorData = floors?.map((fl) => {
    return {
      value: fl,
      label: fl === 0 ? "Ground Floor" : `${ordinalSuffixOf(Number(fl))} Floor`,
      total:
        props.project?.unitDetails
          ?.filter((x) => x.floorName === fl && x.unitStatus === "Active")
          ?.reduce((t: any, c: any) => t + Number(c.unitArea || 0), 0) || 0,
      // available: "4500",
      // from: "01/01/2023",
    };
  });

  let unitData: any = [];
  if (selectedOption?.floorName !== undefined) {
    unitData =
      props.project?.unitDetails
        ?.filter(
          (u) =>
            u.floorName === selectedOption?.floorName &&
            u.propertyID !== "0" &&
            Number(u.propertyID) !== 0 &&
            u.unitStatus === "Active"
        )
        ?.map((un) => {
          const property = props.project?.properties?.find(
            (pp) => pp.propertyID?.toString() === un.propertyID?.toString()
          );

          return {
            value: un.unitID,
            label: un.unitName,
            total: un.unitArea,
            available: property?.availableUnits,
            from:
              property?.availablefromdate &&
                property?.availablefromdate !== "1970-01-01T00:00:00.000Z"
                ? moment(property?.availablefromdate).format("DD-MM-YYYY")
                : "",
          };
        }) || [];
  }

  const renderSelectItems = ({ item }: any) => (
    <div className="border-bottom py-2">
      <div className="mb-1">{item.label}</div>
      <div className="d-flex align-items-center font-xxxs">
        <div className="mr-2 pr-1">
          <span>Total Sq. Ft:</span> {item.total}
        </div>
        {item.available && (
          <div className="mr-2 pr-1">
            <span>Avail:</span> {item.available}
          </div>
        )}
        {item.from && (
          <div>
            <span>From:</span> {item.from}
          </div>
        )}
      </div>
    </div>
  );

  const findMinDate = (values: any[]) => {
    const momentDates = values
      .filter((fVals: { availableFrom?: string }) => {
        return (
          fVals?.availableFrom && moment(fVals.availableFrom).year() !== 1970
        );
      })
      .map((fVals: { availableFrom: string }) => moment(fVals.availableFrom));

    let formattedMinDate = "";
    if (momentDates.length > 0) {
      // Find the minimum date
      const minDate = moment.min(momentDates);
      // Format the minimum date in DD-MM-YYYY format
      formattedMinDate = minDate.format("DD-MM-YYYY");
    }
    return formattedMinDate;
  };

  useEffect(() => {
    if (date) {
      setErrorMessage("");
    }
  }, [date]);

  const showTenants = async () => {
    if (date) {
      setFilteredTenantsLoading(true);
      const data = await api.tenancy.getTenantsByDate({
        tenancyLeaseFromExpiryDate: date
          ? moment(date).startOf("day").toDate()
          : undefined,
        tenancyLeaseToExpiryDate: date
          ? moment(date).endOf("day").toDate()
          : undefined,
        projectID: props.project?.projectID,
      });

      setFilteredTenants(data.projectWiseTenantDetails);
      setFilteredUnitDetails(data.unitDetails);
      setFilteredTenantsLoading(false);
    } else {
      setErrorMessage("Date is required");
    }
  };

  const onreset = () => {
    setDate(undefined);
    setErrorMessage("");
    setFilteredTenants(undefined);
    setFilteredUnitDetails(undefined);
  };

  const openDeveloperTab = (
    e: React.MouseEvent<HTMLSpanElement>,
    developerId: string
  ) => {
    e.stopPropagation();
    if (props.onClose) {
      props.onClose();
    }
    window.scrollTo(0, 0);
    dispatch(
      setDashboardSearchData({
        selectedDevelopers: [developerId],
        selectedTab: "Developers",
      })
    );
  };

  // Base width for consistency (in pixels)
  const baseWidth = 250;
  // Maximum width for the bars (in pixels)
  const maxWidth = 1000;

  return (
    <>
      <div className="pt-4">
        <div className="container-fluid">

          <ProjectOverviewBasicDetails
            canAddTenancyOrLease={canAddTenancyOrLease}
            findMinDate={findMinDate}
            floorData={floorData}
            handleClickpopup={handleClickpopup}
            isShown={isShown}
            mainSlider={mainSlider}
            onRemove={onRemove}
            onSelect={onSelect}
            openDeveloperTab={openDeveloperTab}
            project={props.project}
            renderSelectItems={renderSelectItems}
            selectedOption={selectedOption}
            setIsShown={setIsShown}
            setMainSlider={setMainSlider}
            setSelectedOption={setSelectedOption}
            setShowAddOption={setShowAddOption}
            setShowAddSingleOption={setShowAddSingleOption}
            setThumbSlider={setThumbSlider}
            showAddOption={showAddOption}
            showAddSingleOption={showAddSingleOption}
            thumbSlider={thumbSlider}
            unitData={unitData}
            settings={settings}
            selectedProperty={props.selectedProperty}
            thumbSettings={thumbSettings}
            allUnit={props.allUnit}
            availableUnit={props.availableUnit}
            isMerging={props.isMerging}
            isSpliting={props.isSpliting}
            mergePropeties={props.mergePropeties}
            showDetailsOnly={props.showDetailsOnly}
            splitUnitsAndProperties={props.splitUnitsAndProperties}
          />


          <div className="row">
            <div className="col-sm-6 px-0">
              <div className="d-block">
                <div className="pt-3">
                  <div className="mb-4">
                    <h5 className="font-weight-semibold mb-2">
                      About Property
                    </h5>

                    <p className="font-sm">
                      {props.project?.projectDescription}
                    </p>
                  </div>
                  <div>
                    {!!props.project?.amenities?.length && (
                      <>
                        <h5 className="font-weight-semibold my-3">Amenities</h5>
                        <div className="row">
                          {props.project?.amenities?.map((up) => (
                            <div className="col-sm-4 col-6 font-sm d-flex align-items-center mb-4">
                              <div
                                className="text-center"
                                style={{ width: "34px" }}
                              >
                                <img
                                  src={up.attrOptionImage}
                                  className="icon-security-person"
                                  alt={up.attrOptName}
                                  width="25"
                                  height="25"
                                />
                              </div>
                              <span className="pl-2 ml-1">
                                {" "}
                                {up.attrOptName}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {filteredTenantsLoading && (
            <ContainerLoader
              height={window.innerHeight - 100}
            ></ContainerLoader>
          )}

          {canViewTenancyOrLease &&
            !filteredTenantsLoading &&
            !!props.project?.tenantDetails?.filter(
              (td) => !!td?.unitDetails?.length
            )?.length &&
            props.showDetailsOnly && (

              <ProjectOverviewTenantsGraphView
                baseWidth={baseWidth}
                date={date}
                errorMessage={errorMessage}
                expandedRows={expandedRows}
                filteredTenants={filteredTenants}
                filteredUnitDetails={filteredUnitDetails}
                maxWidth={maxWidth}
                onreset={onreset}
                props={props}
                setDate={setDate}
                showTenants={showTenants}
                toggleRow={toggleRow}
              />

            )}

          <div className="row border-top pt-5">
            {props.project?.projectFloorImage && (
              <>
                <div className="col-12 px-0">
                  <h2 className="mb-4" style={{ fontSize: "21px" }}>
                    Floor Plan
                  </h2>
                </div>

                <div className="col px-0 mb-3" style={{ maxWidth: "626px" }}>
                  <img
                    src={props.project?.projectFloorImage}
                    className="icon-security-person"
                    alt={props.project?.projectName}
                    width="626"
                  />
                </div>
              </>
            )}
          </div>

          {canViewTenancyOrLease && (
            <TenancyTimeline
              projectID={props.project?.projectID}
              projectName={props.project?.projectName}
              className="row border-top pt-5"
              showTitle={true}
            ></TenancyTimeline>
          )}

          <ProjectOverviewLocationMap
            isMapEnabled={isMapEnabled}
            props={props}
            setIsMapEnabled={setIsMapEnabled}
          />

        </div>
      </div>
    </>
  );
};

export default ProjectOverview;


